import { Theme } from '@parkingbase/ecommerce-api-demo';


// ─── Types ───────────────────────────────────────────────────────────────────

interface HSL {
  h: number;
  s: number;
  l: number;
}

type ColorRole =
  | 'fixed'
  | 'brand-primary'
  | 'brand-primary-dark'
  | 'brand-primary-light'
  | 'accent'
  | 'error'
  | 'warning'
  | 'white'
  | 'black'
  | 'text-primary'
  | 'gray'
  | 'gray-light'
  | 'gray-lighter'
  | 'gray-dark'
  | 'gray-darker'
  | 'header-bg'
  | 'tint'
  | 'default';

// ─── HSL Conversion ──────────────────────────────────────────────────────────

export function hexToHSL(hex: string): HSL {
  hex = hex.replace(/^#/, '');
  if (hex.length === 3)
    hex = hex
      .split('')
      .map((c) => c + c)
      .join('');

  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

export function hslToHex(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * Math.max(0, Math.min(1, color)))
      .toString(16)
      .padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

// ─── Role-based Color Transformation ─────────────────────────────────────────

function generateDarkModeColor(hex: string, role: ColorRole): string {
  if (!hex || hex === 'transparent' || hex === '') return hex;

  const { h, s, l } = hexToHSL(hex);

  switch (role) {
    case 'fixed':
      return hex;

    case 'brand-primary':
      return hslToHex(h, Math.min(s + 10, 85), Math.min(l + 20, 65));

    case 'brand-primary-dark':
      return hslToHex(h, Math.min(s + 5, 80), Math.min(l + 15, 55));

    case 'brand-primary-light':
      return hslToHex(h, Math.min(s, 35), Math.max(l - 70, 13));

    case 'accent':
      return hslToHex(h, Math.min(s, 80), Math.min(l, 40));

    case 'error':
      return hslToHex(h, Math.min(s + 5, 85), Math.min(Math.max(l, 50), 60));

    case 'warning':
      return hslToHex(h, Math.min(s + 5, 85), Math.min(Math.max(l, 50), 60));

    case 'white':
      return hslToHex(220, 12, 10);

    case 'black':
      return hslToHex(0, 0, 92);

    case 'text-primary':
      return hslToHex(h, Math.max(5, Math.min(s, 15)), 90);

    case 'gray':
      return hslToHex(h, Math.min(s, 10), 35);

    case 'gray-light':
      return hslToHex(h, Math.min(s, 10), 22);

    case 'gray-lighter':
      return hslToHex(h, Math.min(s, 8), 14);

    case 'gray-dark':
      return hslToHex(h, Math.min(s, 12), 65);

    case 'gray-darker':
      return hslToHex(h, Math.min(s, 8), 88);

    case 'header-bg':
      return hslToHex(h, Math.min(s + 5, 60), Math.max(l - 20, 12));

    case 'tint':
      return hslToHex(h, Math.min(s, 40), Math.max(l - 25, 10));

    default:
      return hslToHex(h, s, 100 - l);
  }
}

// ─── Theme Property → Role Mapping ──────────────────────────────────────────

const COLOR_ROLE_MAP: Record<string, ColorRole> = {
  colorAccent: 'accent',
  colorBrandError: 'error',
  colorBrandWarn: 'warning',
  colorBrandWhite: 'white',
  colorGray: 'gray',
  colorGrayDark: 'gray-dark',
  colorGrayDarker: 'gray-darker',
  colorGrayLight: 'gray-light',
  colorGrayLighter: 'gray-lighter',
  colorBlack: 'black',
  colorFont: 'text-primary',
  colorPrimary: 'fixed',
  colorPrimaryDark: 'brand-primary-dark',
  colorPrimaryDarker: 'brand-primary',
  colorPrimaryLight: 'brand-primary-light',
  whiteLabelHeaderColor: 'header-bg',
  whiteLabelBackgroundTintColor: 'tint',
  logoBackgroundColor: 'white',
};

// ─── Main Export ─────────────────────────────────────────────────────────────

/**
 * Transforms a light Theme into a dark Theme by applying HSL manipulations
 * based on each color property's semantic role.
 *
 * The original theme object is not mutated — a new Theme is returned.
 */
/**
 * Default light-mode values for colors the API theme may omit.
 * Without these, the dark transform skips them and the Tailwind
 * CSS-variable defaults (#fff / #000) remain unchanged in dark mode.
 */
const COLOR_DEFAULTS: Partial<Record<string, string>> = {
  colorBrandWhite: '#ffffff',
  colorBlack: '#000000',
};

export function generateDarkTheme(theme: Theme): Theme {
  const dark = { ...theme };

  for (const [key, role] of Object.entries(COLOR_ROLE_MAP)) {
    const value = (theme[key as keyof Theme] as string) || COLOR_DEFAULTS[key];
    if (value && typeof value === 'string') {
      (dark as Record<string, unknown>)[key] = generateDarkModeColor(value, role);
    }
  }

  return dark;
}
